import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { MessageBoxDialogModule, MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/messagebox';
import { UserService } from '../services/user/user-svc.ng';
import { AuthService } from '../services/auth/auth-svc.ng';
import { UserInfo } from './ui/user-info/user-info.ng';
import { RoutePaths } from './app.routes';
import { BUILD_NOTES, BUILD_TIME } from '../services/version/version.service';
import { LoginDialogModule, LoginDialogService } from '../modules/login/login-dlg/login-dlg.ng';
import { CreateAccountDialogModule, CreateAccountDialogService } from '../modules/login/ui/create-account-dlg/create-account-dlg.ng';
import { SplashDialogModule, SplashService } from '@polk_county_public_schools/ngx-pcps-dialogs/splash';
import { ErrorService } from '../services/error/error.service';
import { AuctionService } from '../services/auction/auction-svc.ng';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatToolbarModule,
        MatIconModule,
        RouterOutlet,
        UserInfo,
        LoginDialogModule,
        CreateAccountDialogModule,
        SplashDialogModule,
    ],
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [DatePipe],
})
export class AppComponent {
    // #region Injects

    protected readonly auctionSvc = inject(AuctionService);
    protected readonly authSvc = inject(AuthService);
    protected readonly confDlgSvc = inject(ConfirmationDialogService);
    protected readonly createActDlgSvc = inject(CreateAccountDialogService);
    protected readonly datePipe = inject(DatePipe);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly loginDlgSvc = inject(LoginDialogService);
    protected readonly msgboxDlgSvc = inject(MessageBoxDialogService);
    protected readonly router = inject(Router);
    protected readonly splashSvc = inject(SplashService);
    protected readonly userSvc = inject(UserService);

    // #endregion

    // #region Properties

    protected readonly isLoggedIn = computed(() => !!this.user()?.AuthToken);
    protected readonly selectedAuction = computed(() => this.auctionSvc.selectedAuction());
    protected readonly user = computed(() => this.userSvc.user());
    protected readonly buildNotes = BUILD_NOTES || '';
    protected readonly buildTime = BUILD_TIME;

    // #endregion

    // #region About

    about() {
        this.msgboxDlgSvc
            .Open({
                TitleText: 'About...',
                MessageText: `The JAuction Application is a custom designed website intended to assist with the running and management of auctions for fund raising events. Issues or questions regarding the system can be directed to <a href="mail:karolsch@karoltech.net">karolsch@karoltech.net</a>.<br /><br />Build time: ${this.datePipe.transform(this.buildTime, 'M/d/yyyy h:mm a')}${this.buildNotes ? `<br />Build Notes:<br />${this.buildNotes}` : ''}`,
            })
            .subscribe();
    }

    // #endregion

    // #region Create Account

    createAccount() {
        // Launch the create account dialog.
        this.createActDlgSvc
            .Open({
                TitleText: 'Create New Account...',
                maxWidth: '600px',
                width: '95%',
            })
            .subscribe((result) => {
                if (!result) return;

                // Need to send the packet to the server for creating the account and mailing out the confirmation link.
                this.splashSvc
                    .Open({
                        TitleText: 'Please Wait',
                        MessageText: 'Creating account...<br /><br /><em>(This could take a few seconds.)</em>',
                    })
                    .subscribe((closer) => {
                        this.authSvc.createAccount(result.firstName, result.lastName, result.email, result.password).subscribe({
                            next: (info) => {
                                closer?.close();

                                this.msgboxDlgSvc
                                    .Open({
                                        TitleText: 'Account Created!',
                                        MessageText: `Your account has been created! Please check your email system for an important email from the JAuction System that has a link you must click to verify/validate your account. You will not be able to login until you have done that step. Thank you.`,
                                    })
                                    .subscribe();
                            },
                            error: (err) => {
                                closer?.close();

                                this.errorSvc.handleServiceError(err);
                            },
                        });
                    });
            });
    }

    // #endregion

    // #region Home

    home() {
        const selAuction = this.auctionSvc.selectedAuction()?.Route;

        this.router.navigate([selAuction ? `/${RoutePaths.ViewAuction}/${selAuction}` : `/${RoutePaths.Home}`]);
    }

    // #endregion

    // #region Login

    login() {
        this.loginDlgSvc
            .Open({
                TitleText: 'Log In',
                MessageText: `Please login using the form below.<br /><br />No account? Not a problem! Click the "Create New Account" button and you'll be taken to our new account form instead.`,
                width: '500px',
            })
            .subscribe({
                next: (lresult) => {
                    if (!lresult) return;

                    if (lresult === 'create') {
                        this.createAccount();
                    }
                },
                error: (err) => {
                    this.errorSvc.handleServiceError(err);
                },
            });
    }

    // #endregion

    // #region Logout

    logout() {
        // Get a confirmation before logging out.
        this.confDlgSvc
            .Open({
                TitleText: 'Log out?',
                MessageText: `You are about to logout from the JAuction system. Are you sure you wish to logout?`,
                OKText: 'Yes - Logout',
                CancelText: 'No - Stay Logged In',
            })
            .subscribe((result) => {
                if (!result) return;

                this.authSvc.logout();
            });
    }

    // #endregion
}
